/**
 * Personal profile store — per-user expertise, certifications, tools, and
 * organizational alignment claims that the user manages themselves.
 *
 * Persistence: Supabase `public.user_profiles` (00079 migration). localStorage
 * acts as a synchronous read cache so callers don't have to be async.
 * Mutations fire-and-forget Supabase upserts so profile edits survive
 * across sessions and devices.
 *
 * This is distinct from the admin-curated catalogs at:
 *   - lib/expertiseCatalog.ts        (the catalog of available expertise)
 *   - lib/certificationsCatalog.ts   (the catalog of available certifications)
 *   - lib/costPlanningAccounts.ts    (admin only)
 *
 * The user picks from those catalogs to populate THEIR profile here.
 */

import type { ResourceExpertise, ResourceCertificationClaim } from '@/types/ppm'
import { getUserProfileRow, upsertUserProfileRow } from '@/services/supabaseService'
import { setActiveDateFormat, toDateFormatId, type DateFormatId } from '@/lib/dateFormat'

const isSupabaseMode = (): boolean => import.meta.env.VITE_DATA_SOURCE === 'supabase'

export interface MyProfile {
  /** Stable user id this profile belongs to. */
  userId: string
  /** Free-text job-title style label. */
  title?: string
  /** Self-described summary — surfaces on team rosters. */
  bio?: string
  /** Tools / technologies the user lists themselves with. Free-text array
   *  (no admin catalog yet — keeps the bar low for self-service). */
  tools: string[]
  /** Organizational alignment — the org id (from getOrganizations) the user
   *  ties themselves to. Optional; defaults to the org from currentUser. */
  organizationId?: string
  /** Display name resolved at write time. */
  organizationName?: string
  /** Expertise claims (mandatory/preferential + 1-5). */
  expertise: ResourceExpertise[]
  /** Certification claims (Yes/No + optional dates). */
  certifications: ResourceCertificationClaim[]
  /** How this user wants dates shown across the app. Blank keeps each
   *  screen's own default. */
  dateFormat?: DateFormatId | null
  /** ISO timestamp of last edit. */
  updatedAt: string
}

const NS = 'ppm-my-profile:'

function key(userId: string) {
  return `${NS}${userId}`
}

export function getMyProfile(userId: string): MyProfile {
  try {
    const raw = localStorage.getItem(key(userId))
    if (raw) {
      const parsed = JSON.parse(raw)
      if (parsed && typeof parsed === 'object') {
        return {
          userId,
          title: typeof parsed.title === 'string' ? parsed.title : undefined,
          bio: typeof parsed.bio === 'string' ? parsed.bio : undefined,
          tools: Array.isArray(parsed.tools) ? parsed.tools.filter((t: unknown) => typeof t === 'string') : [],
          organizationId: typeof parsed.organizationId === 'string' ? parsed.organizationId : undefined,
          organizationName: typeof parsed.organizationName === 'string' ? parsed.organizationName : undefined,
          expertise: Array.isArray(parsed.expertise) ? parsed.expertise : [],
          certifications: Array.isArray(parsed.certifications) ? parsed.certifications : [],
          dateFormat: toDateFormatId(parsed.dateFormat),
          updatedAt: typeof parsed.updatedAt === 'string' ? parsed.updatedAt : new Date().toISOString(),
        }
      }
    }
  } catch { /* ignore */ }
  return {
    userId,
    tools: [],
    expertise: [],
    certifications: [],
    updatedAt: new Date().toISOString(),
  }
}

export function saveMyProfile(profile: MyProfile): void {
  const next: MyProfile = { ...profile, updatedAt: new Date().toISOString() }
  try { localStorage.setItem(key(profile.userId), JSON.stringify(next)) } catch { /* ignore */ }
  if (isSupabaseMode()) {
    upsertUserProfileRow({
      userId: next.userId,
      title: next.title,
      bio: next.bio,
      organizationId: next.organizationId,
      organizationName: next.organizationName,
      tools: next.tools,
      expertise: next.expertise,
      certifications: next.certifications,
      dateFormat: next.dateFormat ?? null,
    }).catch((err) =>
      console.warn('user_profiles upsert failed; localStorage cache still updated', err),
    )
  }
}

/**
 * Pulls the user's profile from Supabase into the localStorage cache.
 * Call once on mount of pages that read profile (e.g. /my-profile). Safe
 * to call in non-Supabase modes — exits early. Errors fall back to cache.
 */
export async function syncMyProfileFromSupabase(userId: string): Promise<void> {
  if (!isSupabaseMode()) return
  try {
    const row = await getUserProfileRow(userId)
    if (!row) return
    const profile: MyProfile = {
      userId,
      title: row.title ?? undefined,
      bio: row.bio ?? undefined,
      organizationId: row.organization_id ?? undefined,
      organizationName: row.organization_name ?? undefined,
      tools: Array.isArray(row.tools) ? row.tools.filter((t): t is string => typeof t === 'string') : [],
      expertise: Array.isArray(row.expertise) ? (row.expertise as ResourceExpertise[]) : [],
      certifications: Array.isArray(row.certifications) ? (row.certifications as ResourceCertificationClaim[]) : [],
      dateFormat: toDateFormatId(row.date_format),
      updatedAt: row.updated_at,
    }
    try { localStorage.setItem(key(userId), JSON.stringify(profile)) } catch { /* ignore */ }
  } catch (err) {
    console.warn('user_profiles sync from Supabase failed; using cached profile', err)
  }
}

/**
 * Make `userId`'s date format the one the app formats with. Reads the cached
 * profile, so it is safe to call during render; call again after
 * syncMyProfileFromSupabase or a save to pick up a change.
 */
export function applyDateFormatForUser(userId: string | null | undefined): DateFormatId | null {
  const format = userId ? (getMyProfile(userId).dateFormat ?? null) : null
  setActiveDateFormat(format)
  return format
}
